import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { waServicesQuery } from './baseQueries';
import { RecordResponse } from '../models/ServiceTypes';
import { WARootState } from './store';
import { MimeType } from '@/utils/fileValidation';
import { createTypedBlob } from '@/utils/triggerBlobDownload';

export interface QuickLinkResource {
  Name: string;
  Url: string;
  Description: string | null;
  CreateDate: string;
  IsActive: boolean;
  Photo: string | null;
  IsPromoted: boolean;
}

export interface Terms {
  Description: string;
  Html: string;
}

interface DashboardSidePanel {
  DashboardSidePanelId: number;
  PanelKey: string;
  Title: string;
  SortOrder: number;
  IsVisible: boolean;
  Settings: string | null;
}
export interface QuickLinkSettings {
  Limit: number;
  Sort: {
    Direction: 'asc' | 'desc';
    Property: 'CreateDate' | 'Name';
  };
}

type DashboardSidePanelsResponse = RecordResponse<DashboardSidePanel>;

const QUICK_LINK_SORT_PROPERTIES: QuickLinkSettings['Sort']['Property'][] = [
  'CreateDate',
  'Name',
];
const QUICK_LINK_SORT_DIRECTIONS: QuickLinkSettings['Sort']['Direction'][] = [
  'asc',
  'desc',
];

export const toQuickLinkSettings = (
  value: unknown,
  defaultSettings: QuickLinkSettings,
): QuickLinkSettings => {
  if (!value || typeof value !== 'object') {
    return defaultSettings;
  }

  const partial = value as Partial<QuickLinkSettings>;
  const partialSort = partial.Sort;

  return {
    Limit:
      typeof partial.Limit === 'number' ? partial.Limit : defaultSettings.Limit,
    Sort: {
      Property:
        partialSort?.Property != null &&
        QUICK_LINK_SORT_PROPERTIES.includes(partialSort.Property)
          ? partialSort.Property
          : defaultSettings.Sort.Property,
      Direction:
        partialSort?.Direction != null &&
        QUICK_LINK_SORT_DIRECTIONS.includes(partialSort.Direction)
          ? partialSort.Direction
          : defaultSettings.Sort.Direction,
    },
  };
};

export interface AdvisorQuickLink {
  UserExternalLinkId: number;
  Name: string;
  Url: string;
  Description: string | null;
  Photo: string | null;
  IsPromoted: boolean;
  CreateDate: string;
  UpdateDate: string;
}

export type QuickLinkResourceResponse = RecordResponse<QuickLinkResource>;
export type AdvisorQuickLinkResponse = RecordResponse<AdvisorQuickLink>;

type RawAdvisorQuickLinkResponse = Omit<AdvisorQuickLinkResponse, 'Records'> & {
  Records?: AdvisorQuickLink[] | null;
};

type ServicesBaseQuery = (
  arg: Parameters<typeof waServicesQuery>[0],
) => ReturnType<typeof waServicesQuery>;

const isEmbeddableBundle = import.meta.env.VITE_WIDGET === 'external';

const buildResourcePhotoUrl = (servicesUrl: string, photo: string): string => {
  const trimmedPhoto = photo.trim();
  if (/^(?:blob:|data:|https?:\/\/)/i.test(trimmedPhoto)) {
    return trimmedPhoto;
  }

  return `${servicesUrl.replace(/\/+$/, '')}/${trimmedPhoto.replace(/^\/+/, '')}`;
};

const readServicesBaseUrl = (state: WARootState): string => {
  const fallback = String(import.meta.env.VITE_SERVICES_URL ?? '').trim();
  return state.appView.WaInfo?.ApiUrl ?? fallback;
};

const missingRoleError = (): FetchBaseQueryError => ({
  status: 'CUSTOM_ERROR',
  error: 'Current role is unavailable',
});

const resolveRecordPhotos = async <T extends { Photo: string | null }>(
  records: T[],
  state: WARootState,
  baseQuery: ServicesBaseQuery,
): Promise<T[]> => {
  const currentRole = state.auth.currentRole;
  const baseUrl = readServicesBaseUrl(state);
  const token = state.appView.UserInfo?.Token;

  return Promise.all(
    records.map(async (record) => {
      if (!record.Photo) return record;

      try {
        if (isEmbeddableBundle) {
          const photoUrl = buildResourcePhotoUrl(baseUrl, record.Photo);
          if (/^(?:blob:|data:)/i.test(photoUrl)) {
            return { ...record, Photo: photoUrl };
          }

          const headers = new Headers({ Accept: 'image/*' });
          if (token) {
            headers.set('Authentication', token);
            headers.set(
              'Currentrole',
              currentRole ? currentRole.toString().toLowerCase() : 'investor',
            );
          }

          const response = await fetch(photoUrl, {
            method: 'GET',
            headers,
            credentials: 'same-origin',
          });

          if (response.ok) {
            return {
              ...record,
              Photo: URL.createObjectURL(await response.blob()),
            };
          }

          return { ...record, Photo: null };
        }

        const photoResult = await baseQuery({
          url: baseUrl + record.Photo,
          method: 'GET',
          responseHandler: async (response) => response.blob(),
        });

        if (photoResult.data) {
          const blob = createTypedBlob(
            photoResult.data as Blob,
            null,
            MimeType.Jpeg,
          );
          return {
            ...record,
            Photo: URL.createObjectURL(blob),
          };
        }
      } catch (error) {
        console.error('Error loading photo:', error);
      }

      return record;
    }),
  );
};

export const quickLinksResourcesApi = createApi({
  reducerPath: 'quickLinksResourcesApi',
  baseQuery: waServicesQuery,

  endpoints: (builder) => ({
    /**
     * Fetches QuickLinks (isResource=false).
     * Used by dashboardQuickLinks widget.
     */
    // RTK Query uses void so callers can omit the argument.
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getQuickLinks: builder.query<QuickLinkResourceResponse, void>({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        if (!currentRole) return { error: missingRoleError() };

        const result = await baseQuery({
          url: `/api/${currentRole}/QuickLinks?isResource=false`,
          method: 'GET',
        });

        if (result.error) return { error: result.error };

        return { data: result.data as QuickLinkResourceResponse };
      },
    }),
    // RTK Query uses void so callers can omit the argument.
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getQuickLinkSettings: builder.query<QuickLinkSettings, void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/user/dashboardSidePanels`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        const data = result.data as DashboardSidePanelsResponse;
        const quickLinksPanel = data.Records.find(
          (panel) => panel.PanelKey === 'investor-quick-links',
        );

        const defaultSettings: QuickLinkSettings = {
          Limit: 5,
          Sort: { Direction: 'asc', Property: 'Name' },
        };

        // Default settings
        if (!quickLinksPanel?.Settings) {
          return {
            data: defaultSettings,
          };
        }

        try {
          const parsed: unknown = JSON.parse(quickLinksPanel.Settings);
          return { data: toQuickLinkSettings(parsed, defaultSettings) };
        } catch {
          return {
            data: defaultSettings,
          };
        }
      },
    }),
    /**
     * Fetches Resources (isResource=true) and processes photos.
     * Used by dashboardResources widget.
     */
    // RTK Query uses void so callers can omit the argument.
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getResources: builder.query<QuickLinkResourceResponse, void>({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        if (!currentRole) return { error: missingRoleError() };

        const result = await baseQuery({
          url: `/api/${currentRole}/QuickLinks?isResource=true`,
          method: 'GET',
        });

        if (result.error) return { error: result.error };

        const records = (result.data as QuickLinkResourceResponse).Records;
        const recordsWithPhotos = await resolveRecordPhotos(
          records,
          state,
          baseQuery,
        );

        return {
          data: {
            ...(result.data as QuickLinkResourceResponse),
            Records: recordsWithPhotos,
          },
        };
      },
    }),

    getAdvisorQuickLinks: builder.query<AdvisorQuickLinkResponse, undefined>({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/api/user/QuickLinks?isResource=false',
          method: 'GET',
        });

        if (result.error) return { error: result.error };

        const response = result.data as RawAdvisorQuickLinkResponse;
        const records = await resolveRecordPhotos(
          response.Records ?? [],
          getState() as WARootState,
          baseQuery,
        );

        return { data: { ...response, Records: records } };
      },
    }),

    getAdvisorResources: builder.query<AdvisorQuickLinkResponse, undefined>({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/api/advisor/QuickLinks/resources',
          method: 'GET',
        });

        if (result.error) return { error: result.error };

        const response = result.data as RawAdvisorQuickLinkResponse;
        const records = await resolveRecordPhotos(
          response.Records ?? [],
          getState() as WARootState,
          baseQuery,
        );

        return { data: { ...response, Records: records } };
      },
    }),

    // RTK Query uses void so callers can omit the argument.
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getTerms: builder.query<Terms, void>({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole ?? 'account';
        const result = await baseQuery({
          url: `/api/${currentRole}/terms`,
          method: 'GET',
        });

        if (result.error) return { error: result.error };

        return { data: result.data as Terms };
      },
    }),
  }),
});

export const {
  useGetQuickLinksQuery,
  useGetQuickLinkSettingsQuery,
  useGetResourcesQuery,
  useGetAdvisorQuickLinksQuery,
  useGetAdvisorResourcesQuery,
  useGetTermsQuery,
} = quickLinksResourcesApi;
